import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileArchive, Code, Database, Settings, CheckCircle, AlertCircle, Github, Package } from 'lucide-react';
import Navbar from '../components/Navbar';

function BackupPage({ user, setUser }) {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    setDownloading(true);
    
    // Trigger download
    const link = document.createElement('a');
    link.href = '/downloads/tradepulse-backup.zip';
    link.download = 'tradepulse-backup.zip';
    link.click();
    
    setTimeout(() => setDownloading(false), 2000);
  };

  const backupInfo = {
    filename: 'tradepulse-backup.zip',
    size: '580 KB',
    date: new Date().toLocaleDateString('tr-TR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    files: 226,
    description: 'Tam yedek - Tüm kaynak kodları ve ayarlar'
  };

  const includes = [
    { icon: Code, title: 'Frontend Kodu', desc: 'React uygulaması (src, components, pages)', color: 'text-blue-400' },
    { icon: Code, title: 'Backend Kodu', desc: 'FastAPI server, API endpoints, models', color: 'text-green-400' },
    { icon: Database, title: 'Veritabanı Modelleri', desc: 'MongoDB schemas ve seed data', color: 'text-purple-400' },
    { icon: Settings, title: 'Konfigürasyon', desc: 'package.json, requirements.txt', color: 'text-yellow-400' },
    { icon: Package, title: 'Bağımlılıklar', desc: 'Tüm npm ve pip paketleri listesi', color: 'text-orange-400' },
    { icon: FileArchive, title: 'Dokümantasyon', desc: 'YEDEKLEME_KILAVUZU.md dahil', color: 'text-pink-400' },
  ];

  const instructions = [
    {
      step: 1,
      title: 'ZIP Dosyasını İndir',
      desc: 'Aşağıdaki yeşil butona tıklayarak yedek dosyanızı indirin'
    },
    {
      step: 2,
      title: 'Arşivi Çıkar',
      desc: 'ZIP dosyasını bilgisayarınızda istediğiniz klasöre çıkarın'
    },
    {
      step: 3,
      title: 'API Anahtarlarını Ekle',
      desc: 'YEDEKLEME_KILAVUZU.md dosyasındaki .env bilgilerini kullanın'
    },
    {
      step: 4,
      title: 'Bağımlılıkları Yükle',
      desc: 'Backend: pip install -r requirements.txt | Frontend: yarn install'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar user={user} setUser={setUser} />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Github size={48} className="text-blue-400" />
            <h1 className="text-4xl font-bold">Proje Yedekleme</h1>
          </div>
          <p className="text-gray-400 text-lg">
            TradePulse Trading Platform - Tam Kaynak Kodu Yedekleme
          </p>
        </div>

        {/* Main Download Card */}
        <div className="bg-gray-800 rounded-xl p-8 mb-8 border border-gray-700 shadow-2xl">
          <div className="flex items-start gap-6 mb-6">
            <div className="bg-blue-600 p-4 rounded-lg">
              <FileArchive size={48} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{backupInfo.filename}</h2>
              <p className="text-gray-400 mb-4">{backupInfo.description}</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-gray-400" />
                  <span className="text-gray-300">{backupInfo.files} dosya</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileArchive size={16} className="text-gray-400" />
                  <span className="text-gray-300">{backupInfo.size}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-400" />
                  <span className="text-gray-300">{backupInfo.date}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className={`w-full py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-3 ${
              downloading 
                ? 'bg-gray-600 cursor-not-allowed' 
                : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-lg hover:scale-[1.02]'
            }`}
          >
            <Download size={24} />
            {downloading ? 'İndiriliyor...' : 'Yedeği İndir (.zip)'}
          </button>
        </div>

        {/* What's Included */}
        <div className="bg-gray-800 rounded-xl p-8 mb-8 border border-gray-700">
          <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Package className="text-blue-400" />
            Yedekte Neler Var?
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {includes.map((item, idx) => (
              <div key={idx} className="bg-gray-750 rounded-lg p-4 border border-gray-600 hover:border-gray-500 transition-colors">
                <div className="flex items-start gap-3">
                  <item.icon size={24} className={item.color} />
                  <div>
                    <h4 className="font-semibold mb-1">{item.title}</h4>
                    <p className="text-sm text-gray-400">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
          <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Settings className="text-yellow-400" />
            Kurulum Adımları
          </h3>
          <div className="space-y-4">
            {instructions.map((item) => (
              <div key={item.step} className="flex gap-4 items-start">
                <div className="bg-blue-600 rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Warning */}
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-xl p-6 mt-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
            <div>
              <h4 className="font-bold text-yellow-400 mb-2">Önemli Güvenlik Notu</h4>
              <p className="text-gray-300 text-sm">
                Bu yedek dosyası içinde <strong>API anahtarları ve hassas bilgiler</strong> bulunmaktadır. 
                YEDEKLEME_KILAVUZU.md dosyasındaki bilgileri güvenli bir yerde saklayın ve 
                <strong className="text-yellow-400"> asla başkalarıyla paylaşmayın</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Geri Dön
          </button>
        </div>
      </div>
    </div>
  );
}

export default BackupPage;
